import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import { formatDate } from '../../helpers/UtilsFunctions';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import { AlertDialog } from '../Common';
import '../../styles/loading-animations.css';

const THEME_GREEN = '#009391';
const PAGE_SIZES = [5, 10, 25, 50];

const formatStatusLabel = (value) => {
  if (!value) return '-';
  const normalized = String(value).toLowerCase();
  if (normalized === 'active') return 'Active';
  if (normalized === 'inactive' || normalized === 'deactive') return 'Deactive';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusBadgeStyle = (value, mode) => {
  const base = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid transparent',
  };
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
      color: '#059669',
      borderColor: mode === 'dark' ? 'rgba(16,185,129,0.4)' : '#a7f3d0',
    };
  }
  if (normalized === 'inactive' || normalized === 'deactive') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2',
      color: '#dc2626',
      borderColor: mode === 'dark' ? 'rgba(239,68,68,0.4)' : '#fecaca',
    };
  }
  return {
    ...base,
    background: mode === 'dark' ? '#1f2937' : '#f3f4f6',
    color: mode === 'dark' ? '#e5e7eb' : '#374151',
    borderColor: mode === 'dark' ? '#374151' : '#e5e7eb',
  };
};

const extractDocuments = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.documents)) return response.documents;
  if (Array.isArray(response?.data?.documents)) return response.data.documents;
  return [];
};

const normalizeDocument = (doc, index) => {
  const fileName =
    doc?.original_name ||
    doc?.file_name ||
    doc?.document_name ||
    doc?.name ||
    `Document ${index + 1}`;
  const title = doc?.title || doc?.document_title || fileName;
  const id = doc?.id || doc?.document_id || doc?.uuid || `${title}-${index}`;
  const uploadedAt =
    doc?.created_at || doc?.uploaded_at || doc?.created_on || doc?.createdAt || null;

  return { id, title, fileName, uploadedAt, raw: doc };
};

const getDocumentId = (doc) => {
  const id = doc?.raw?.id ?? doc?.raw?.document_id ?? doc?.id;
  if (id == null || id === '') return null;
  return id;
};

const SiteDetail = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteId } = useParams();

  const [site, setSite] = useState(() => location.state?.site || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [documents, setDocuments] = useState([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [docsPage, setDocsPage] = useState(0);
  const [docsPageSize, setDocsPageSize] = useState(10);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const [deleteDocDialog, setDeleteDocDialog] = useState({ isOpen: false, doc: null });
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  const [submittedForms, setSubmittedForms] = useState([]);
  const [isSubmittedFormsLoading, setIsSubmittedFormsLoading] = useState(false);
  const [submittedFormsError, setSubmittedFormsError] = useState('');
  const [submittedFormsPage, setSubmittedFormsPage] = useState(0);
  const [submittedFormsPageSize, setSubmittedFormsPageSize] = useState(10);

  const fetchSiteDetail = useCallback(
    async ({ refreshDocumentsOnly = false } = {}) => {
      if (!siteId) return;
      try {
        if (refreshDocumentsOnly) {
          setIsDocumentsLoading(true);
        } else {
          setIsLoading(true);
        }
        setError('');
        setDocumentsError('');
        const response = await dataAPI.getSiteDetail(siteId, { showLoading: false });
        if (response?.status === 'success' && response?.data) {
          setSite(response.data);
          const docs = extractDocuments(response.data).map((item, index) =>
            normalizeDocument(item, index),
          );
          setDocuments(docs);
        } else {
          setSite(null);
          setDocuments([]);
          setError(response?.message || 'Site not found.');
        }
      } catch (apiError) {
        if (!refreshDocumentsOnly) {
          setSite(null);
          setError(apiError?.message || 'Failed to load site details.');
        }
        setDocuments([]);
        setDocumentsError(apiError?.message || 'Unable to load documents right now.');
      } finally {
        if (refreshDocumentsOnly) {
          setIsDocumentsLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [siteId],
  );

  useEffect(() => {
    fetchSiteDetail();
  }, [fetchSiteDetail]);

  useEffect(() => {
    setDocsPage(0);
  }, [documents.length, docsPageSize]);

  useEffect(() => {
    const fetchSubmittedForms = async () => {
      const targetSiteId = String(siteId || site?.id || '').trim();
      if (!targetSiteId) {
        setSubmittedForms([]);
        return;
      }

      try {
        setIsSubmittedFormsLoading(true);
        setSubmittedFormsError('');
        const response = await dataAPI.getDigitalFormSubmissionsByDestination(
          {
            form_destination: 'site',
            site_id: targetSiteId,
          },
          { showLoading: false },
        );
        const apiRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
        setSubmittedForms(apiRows);
      } catch (fetchError) {
        console.error('Error fetching site form submissions:', fetchError);
        setSubmittedForms([]);
        setSubmittedFormsError(
          fetchError?.message || 'Unable to load form submissions right now.',
        );
      } finally {
        setIsSubmittedFormsLoading(false);
      }
    };

    if (!isLoading && !error && site) {
      fetchSubmittedForms();
    }
  }, [siteId, site?.id, isLoading, error, site]);

  useEffect(() => {
    setSubmittedFormsPage(0);
  }, [submittedForms.length, submittedFormsPageSize]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    const time = parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const paginatedSubmittedForms = submittedForms.slice(
    submittedFormsPage * submittedFormsPageSize,
    submittedFormsPage * submittedFormsPageSize + submittedFormsPageSize
  );

  const paginatedDocuments = documents.slice(
    docsPage * docsPageSize,
    docsPage * docsPageSize + docsPageSize
  );

  const handleOpenUpload = () => {
    setSelectedFiles([]);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsUploadOpen(true);
  };

  const handleCloseUpload = () => {
    if (isUploading) return;
    setIsUploadOpen(false);
    setSelectedFiles([]);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles((prev) => {
      const existingKeys = new Set(
        prev.map((item) => `${item.file.name}-${item.file.size}-${item.file.lastModified}`),
      );
      const newFiles = files.filter(
        (file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`),
      );
      return [...prev, ...newFiles.map((file) => ({ file }))];
    });

    if (uploadError) setUploadError('');
    event.target.value = '';
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      setUploadError('Please choose at least one file.');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      selectedFiles.forEach((item) => {
        formData.append('documents', item.file);
      });

      const response = await dataAPI.uploadSiteDocument(siteId, formData, { showLoading: false });
      if (response?.status === 'success') {
        await fetchSiteDetail({ refreshDocumentsOnly: true });
        handleCloseUpload();
      } else {
        setUploadError(response?.message || 'Failed to upload document(s).');
      }
    } catch (apiError) {
      setUploadError(apiError?.message || 'Failed to upload document(s).');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (!siteId) return;

    const documentId = getDocumentId(doc);
    if (!documentId) {
      alert('Unable to download this document right now.');
      return;
    }

    try {
      const response = await dataAPI.downloadSiteDocument(siteId, documentId, {
        showLoading: false,
      });

      if (response?.status === 'success' && response?.data?.download_url) {
        window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
        return;
      }

      alert(response?.message || 'Download link is not available at the moment.');
    } catch (apiError) {
      alert(apiError?.message || 'Failed to download the document.');
    }
  };

  const handleDeleteDocumentClick = (doc) => {
    setDeleteDocDialog({ isOpen: true, doc });
  };

  const handleDeleteDocumentConfirm = async () => {
    const doc = deleteDocDialog.doc;
    const documentId = getDocumentId(doc);
    if (!siteId || !documentId || isDeletingDoc) {
      if (!documentId) {
        alert('Unable to delete this document right now.');
      }
      return;
    }

    setIsDeletingDoc(true);
    try {
      const response = await dataAPI.deleteSiteDocument(siteId, documentId, {
        showLoading: false,
      });
      if (response?.status === 'success') {
        const deletedId = response?.data?.document_id ?? documentId;
        setDocuments((prev) =>
          prev.filter((item) => String(getDocumentId(item)) !== String(deletedId)),
        );
        setDeleteDocDialog({ isOpen: false, doc: null });
      } else {
        alert(response?.message || 'Failed to delete document.');
      }
    } catch (apiError) {
      alert(apiError?.message || 'Failed to delete document.');
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const cardStyle = {
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.35)' : '0 6px 18px rgba(0,0,0,0.08)',
  };

  const detailFields = site
    ? [
        { label: 'Site Name', value: site.site_name },
        { label: 'Site Address', value: site.site_address },
        {
          label: 'Status',
          value: (
            <span style={getStatusBadgeStyle(site.status, mode)}>
              {formatStatusLabel(site.status)}
            </span>
          ),
        },
        { label: 'Created By', value: site.created_by_username },
        { label: 'Created At', value: site.created_at ? formatDate(site.created_at) : '-' },
      ]
    : [];

  return (
    <div
      style={{
        padding: '24px',
        minHeight: '100vh',
        background: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        color: mode === 'dark' ? '#fff' : '#111827',
        fontFamily: 'Urbanist, Arial, sans-serif',
      }}
    >
      <button
        type="button"
        onClick={() => navigate(APP_URLS.SITES)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px solid ${mode === 'dark' ? '#333' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '8px 14px',
          background: 'transparent',
          color: mode === 'dark' ? '#fff' : '#111827',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        <ArrowBackIcon style={{ fontSize: 18 }} />
        Back to Sites
      </button>

      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 220px)',
            marginTop: '24px',
          }}
        >
          <img
            src={AppIconBlue}
            alt="Loading"
            className="pulse-animation-icon"
            style={{ width: 46, height: 46 }}
          />
        </div>
      ) : error ? (
        <div
          style={{
            marginTop: '24px',
            border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fecaca'}`,
            borderRadius: '12px',
            padding: '16px',
            color: mode === 'dark' ? '#fecaca' : '#b91c1c',
          }}
        >
          {error}
        </div>
      ) : !site ? (
        <div style={{ marginTop: '24px', ...cardStyle }}>Site not found.</div>
      ) : (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          {/* Site details section */}
          <section style={cardStyle}>
            <h2 style={{ margin: '0 0 20px', fontSize: '22px', fontWeight: 700 }}>Site Details</h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px',
              }}
            >
              {detailFields.map((field) => (
                <div key={field.label}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                      marginBottom: '6px',
                    }}
                  >
                    {field.label}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>{field.value || '-'}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Documents section */}
          <section style={cardStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Documents</h2>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUpload}
                sx={{
                  backgroundColor: THEME_GREEN,
                  borderRadius: '10px',
                  textTransform: 'none',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#007b79' },
                }}
              >
                Add Documents
              </Button>
            </div>

            {documentsError ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#fecaca' : '#b91c1c',
                  fontWeight: 600,
                }}
              >
                {documentsError}
              </div>
            ) : isDocumentsLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px',
                }}
              >
                <img
                  src={AppIconBlue}
                  alt="Loading"
                  className="pulse-animation-icon"
                  style={{ width: 40, height: 40 }}
                />
              </div>
            ) : documents.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                }}
              >
                No documents uploaded yet.
              </div>
            ) : (
              <Paper
                sx={{
                  border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                }}
              >
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thStyle(mode)}>Title</TableCell>
                        <TableCell sx={thStyle(mode)}>File Name</TableCell>
                        <TableCell sx={thStyle(mode)}>Uploaded At</TableCell>
                        <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDocuments.map((doc) => (
                        <TableRow
                          key={doc.id}
                          sx={{
                            '& td': {
                              borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                            },
                          }}
                        >
                          <TableCell sx={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <InsertDriveFileIcon
                                sx={{ fontSize: 18, color: THEME_GREEN, flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: 600 }}>{doc.title}</span>
                            </div>
                          </TableCell>
                          <TableCell sx={tdStyle}>{doc.fileName}</TableCell>
                          <TableCell sx={tdStyle}>
                            {doc.uploadedAt ? formatDate(doc.uploadedAt) : '-'}
                          </TableCell>
                          <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', gap: '4px' }}>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadDocument(doc)}
                                  sx={{ color: THEME_GREEN }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocumentClick(doc)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={documents.length}
                  page={docsPage}
                  onPageChange={(_, newPage) => setDocsPage(newPage)}
                  rowsPerPage={docsPageSize}
                  onRowsPerPageChange={(event) => {
                    setDocsPageSize(parseInt(event.target.value, 10));
                    setDocsPage(0);
                  }}
                  rowsPerPageOptions={PAGE_SIZES}
                  sx={{
                    color: mode === 'dark' ? '#d0d5dd' : '#344054',
                    borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                    backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                  }}
                />
              </Paper>
            )}
          </section>

          {/* Form submissions section */}
          <section style={cardStyle}>
            <h2 style={{ margin: '0 0 20px', fontSize: '22px', fontWeight: 700 }}>
              Form Submissions
            </h2>

            {submittedFormsError ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#fecaca' : '#b91c1c',
                  fontWeight: 600,
                }}
              >
                {submittedFormsError}
              </div>
            ) : isSubmittedFormsLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px',
                }}
              >
                <img
                  src={AppIconBlue}
                  alt="Loading"
                  className="pulse-animation-icon"
                  style={{ width: 40, height: 40 }}
                />
              </div>
            ) : submittedForms.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                }}
              >
                No form submissions found.
              </div>
            ) : (
              <Paper
                sx={{
                  border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
                }}
              >
                <TableContainer sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <Table stickyHeader size="small" aria-label="Site form submissions table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thStyle(mode)}>Form Title</TableCell>
                        <TableCell sx={thStyle(mode)}>Description</TableCell>
                        <TableCell sx={thStyle(mode)}>Submitted By</TableCell>
                        <TableCell sx={thStyle(mode)}>Submitted At</TableCell>
                        <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSubmittedForms.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            '& td': {
                              borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                            },
                          }}
                        >
                          <TableCell sx={{ ...tdStyle, fontWeight: 600 }}>
                            {row.form_title || `Form ${row.client_form_id || row.id}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tdStyle,
                              color: mode === 'dark' ? '#9ca3af' : '#64748b',
                            }}
                          >
                            {row.form_description || '-'}
                          </TableCell>
                          <TableCell sx={tdStyle}>
                            {row.submitted_by_full_name || '-'}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...tdStyle,
                              color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                              fontSize: '12px',
                            }}
                          >
                            {formatDateTime(row.submitted_at)}
                          </TableCell>
                          <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                navigate(APP_URLS.COMPANY_FORM_RECORD_VIEW, {
                                  state: {
                                    form: {
                                      id: row.client_form_id,
                                      title: row.form_title || 'Form Record',
                                      description: row.form_description || '',
                                    },
                                    submission: {
                                      ...row,
                                      answers: row.answers,
                                    },
                                    source: 'site-details',
                                  },
                                })
                              }
                              sx={{
                                backgroundColor: THEME_GREEN,
                                borderRadius: '8px',
                                textTransform: 'none',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: '12px',
                                '&:hover': { backgroundColor: '#007b79' },
                              }}
                            >
                              View form
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={submittedForms.length}
                  page={submittedFormsPage}
                  onPageChange={(_, newPage) => setSubmittedFormsPage(newPage)}
                  rowsPerPage={submittedFormsPageSize}
                  onRowsPerPageChange={(event) => {
                    setSubmittedFormsPageSize(parseInt(event.target.value, 10));
                    setSubmittedFormsPage(0);
                  }}
                  rowsPerPageOptions={PAGE_SIZES}
                  sx={{
                    color: mode === 'dark' ? '#d0d5dd' : '#344054',
                    borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                    backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      margin: 0,
                    },
                  }}
                />
              </Paper>
            )}
          </section>
        </div>
      )}

      <Dialog
        open={isUploadOpen}
        onClose={handleCloseUpload}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: mode === 'dark' ? '#111111' : '#ffffff',
            color: mode === 'dark' ? '#ffffff' : '#2c3e50',
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '20px' }}>Upload Documents</DialogTitle>
        <DialogContent
          dividers
          sx={{ borderColor: mode === 'dark' ? '#1f1f1f' : '#efefef' }}
        >
          {uploadError ? (
            <div
              style={{
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: mode === 'dark' ? 'rgba(244,67,54,0.15)' : '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '8px',
                color: mode === 'dark' ? '#ff8a8a' : '#d32f2f',
                fontSize: '14px',
              }}
            >
              {uploadError}
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '12px',
              flexWrap: 'wrap',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              variant="contained"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              sx={{
                backgroundColor: THEME_GREEN,
                borderRadius: '10px',
                textTransform: 'none',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '14px',
                letterSpacing: '0.3px',
                padding: '8px 18px',
                '&:hover': { backgroundColor: '#007b79' },
              }}
            >
              Choose Files
            </Button>
            <span
              style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#a0aec0' : '#7f8c8d',
              }}
            >
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
                : 'No file chosen'}
            </span>
          </div>

          {selectedFiles.length > 0 ? (
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {selectedFiles.map((item, index) => (
                <div
                  key={`${item.file.name}-${item.file.size}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: `1px solid ${mode === 'dark' ? '#2d2d2d' : '#e0e0e0'}`,
                    backgroundColor: mode === 'dark' ? '#121212' : '#f8fbfb',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.file.name}
                  </span>
                  <Button
                    size="small"
                    onClick={() => handleRemoveSelectedFile(index)}
                    disabled={isUploading}
                    sx={{
                      textTransform: 'none',
                      color: mode === 'dark' ? '#f87171' : '#ef4444',
                      minWidth: 'auto',
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            borderTop: `1px solid ${mode === 'dark' ? '#1f1f1f' : '#efefef'}`,
          }}
        >
          <Button
            onClick={handleCloseUpload}
            disabled={isUploading}
            sx={{
              textTransform: 'none',
              color: mode === 'dark' ? '#ffffff' : '#2c3e50',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocuments}
            variant="contained"
            disabled={selectedFiles.length === 0 || isUploading}
            sx={{
              backgroundColor: THEME_GREEN,
              borderRadius: '10px',
              textTransform: 'none',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.3px',
              '&:hover': { backgroundColor: '#007b79' },
              '&.Mui-disabled': {
                backgroundColor: mode === 'dark' ? '#444' : '#9ca3af',
                color: '#ffffff',
              },
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        isOpen={deleteDocDialog.isOpen}
        onClose={() => !isDeletingDoc && setDeleteDocDialog({ isOpen: false, doc: null })}
        onConfirm={handleDeleteDocumentConfirm}
        type="delete"
        title="Delete Document"
        message="Are you sure you want to delete this document?"
        itemName={deleteDocDialog.doc?.title || ''}
        confirmText={isDeletingDoc ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
      />
    </div>
  );
};

const thStyle = (mode) => ({
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: mode === 'dark' ? '#111111' : '#f9fafb',
  color: mode === 'dark' ? '#ffffff' : '#111827',
});

const tdStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
};

export default SiteDetail;
